import { useCallback, useEffect, useState } from "react";
import { Title } from '../../utils/title';
import { useParams } from "react-router";
import { DriverNavBar } from "./NavBar";
import { fetchApi } from "../../services/api";
import { Loader } from "@progress/kendo-react-indicators";
import { Grid, GridColumn as Column, GridSortChangeEvent } from '@progress/kendo-react-grid';
import DateCell from "../../components/cells/DateCell";
import { SortDescriptor, orderBy } from "@progress/kendo-data-query";
import dayjs from "dayjs";

type RouteComponentParams = {
  driverId: string;
};

type DriverDeviceType = {
  DriverAppDeviceID: string;
  CreatedDateTime: Date;
  Activated: boolean;
  ActivationCode: number | null;
  DeviceName: string;
  IsOutdated: boolean;
  AppVersion: string | null;
  HasPushNotifications: boolean;
  LastRefreshDateTime: Date | null;
  GeofenceArrival: boolean;
  Notifications: { Name: string, Enabled: boolean }[];
}

type DriverDevicesType = {
  Devices: DriverDeviceType[];
  ReceiveQuoteOfferCalls: boolean;
  ReceiveQuoteOfferCallsOnTrip: boolean;
  ReceiveQuoteOfferCallsAuto: boolean;
  ReceiveWeatherAlerts: boolean;
}

const STALE_DAYS = 7;

const Option = ({ enabled, children, title }: { enabled: boolean, children: React.ReactNode, title?: string }) =>
  <span className={`mr-3 text-nowrap${enabled ? '' : ' text-muted'}`} style={enabled ? undefined : { textDecoration: 'line-through' }} title={title ?? (enabled ? 'On' : 'Off')}>
    {children}
  </span>;

const isOutdated = (device: DriverDeviceType) => !!device.AppVersion && device.IsOutdated;

const receivesPush = (device: DriverDeviceType) => device.Activated && device.HasPushNotifications;

const lastUsed = (device: DriverDeviceType) => dayjs.utc(device.LastRefreshDateTime ?? device.CreatedDateTime).valueOf();

const currentDevice = (devices: DriverDeviceType[]) => [...devices]
  .sort((a, b) => Number(b.Activated) - Number(a.Activated) || lastUsed(b) - lastUsed(a))[0];

const isStale = (device: DriverDeviceType) => !device.LastRefreshDateTime || dayjs().diff(dayjs.utc(device.LastRefreshDateTime), 'day') >= STALE_DAYS;

const Value = ({ warn, children, title }: { warn?: boolean, children: React.ReactNode, title?: string }) =>
  warn ? <span className="badge badge-warning" title={title}>{children}</span> : <span title={title}>{children}</span>;

const Field = ({ label, children }: { label: string, children: React.ReactNode }) => <div className="mr-5 mb-2">
  <h6 className="text-uppercase text-muted small mb-1">{label}</h6>
  <div>{children}</div>
</div>;

const DriverDevices = () => {

  const { driverId: driverIdParam } = useParams<RouteComponentParams>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DriverDevicesType>();
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'CreatedDateTime', dir: 'desc' }]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchApi(`/api/Asset/DriverDevices/${driverIdParam}`)
      .then((data: DriverDevicesType) => {
        setLoading(false);
        setData(data);
      })
      .catch(err => {
        alert(err);
        setLoading(false);
      });
  }, [driverIdParam]);

  const onGridSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  useEffect(() => {
    refresh();
  }, [driverIdParam, refresh]);

  const loadingView = <div className="k-pos-absolute k-top-center mt-5">
    <Loader type="converging-spinner" />
  </div>;

  const currentView = (data: DriverDevicesType, device: DriverDeviceType) => <div className="card mb-3">
    <div className="card-body">
      <h6 className="text-uppercase text-muted mb-2">Current Device</h6>
      <div className="d-flex align-items-baseline flex-wrap mb-3">
        <h4 className="mb-0 mr-3">{device.DeviceName}</h4>
        <Value warn={isOutdated(device)} title={isOutdated(device) ? 'Outdated app version' : undefined}>
          {device.AppVersion ? `v${device.AppVersion}` : 'Web'}{isOutdated(device) && ' · Outdated'}
        </Value>
      </div>
      <div className="d-flex flex-wrap">
        <Field label="Last Opened">
          <Value warn={isStale(device)} title={device.LastRefreshDateTime ? dayjs.utc(device.LastRefreshDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm") : undefined}>
            {device.LastRefreshDateTime ? dayjs.utc(device.LastRefreshDateTime).fromNow() : 'Never'}
          </Value>
        </Field>
        <Field label="Quote Offer Calls">
          <Option enabled={data.ReceiveQuoteOfferCalls} title={data.ReceiveQuoteOfferCalls ? 'On' : 'Off, no quote offer calls at all'}>Manual Offers</Option>
          <Option enabled={data.ReceiveQuoteOfferCalls && data.ReceiveQuoteOfferCallsAuto}>Auto Offers</Option>
          <Option enabled={data.ReceiveQuoteOfferCalls && data.ReceiveQuoteOfferCallsOnTrip} title={data.ReceiveQuoteOfferCalls && !data.ReceiveQuoteOfferCallsOnTrip ? 'Off, but still called when on a repositioning trip' : undefined}>While On A Load</Option>
        </Field>
        <Field label="Weather Alerts">
          <Value>{data.ReceiveWeatherAlerts ? 'On' : 'Off'}</Value>
        </Field>
        <Field label="Geofence Arrival">
          <Value>{device.GeofenceArrival ? 'On' : 'Off'}</Value>
        </Field>
      </div>
      <Field label="Notifications">
        {receivesPush(device)
          ? <div className="d-flex flex-wrap">{device.Notifications.map(x => <Option key={x.Name} enabled={x.Enabled}>{x.Name}</Option>)}</div>
          : <Value warn title={device.Activated ? 'This phone isn\'t set up to receive notifications from the app' : undefined}>
            {device.Activated ? 'Off' : `Not Activated (Code ${device.ActivationCode ?? '—'})`}
          </Value>}
      </Field>
    </div>
  </div>;

  const historyView = (devices: DriverDeviceType[], current: DriverDeviceType) => <>
    <h6 className="text-uppercase text-muted mb-2">Device History</h6>
    <Grid
      sortable
      sort={sort}
      data={orderBy(devices, sort)}
      onSortChange={onGridSortChange}
      dataItemKey="DriverAppDeviceID"
    >
      <Column field="CreatedDateTime" title="Created" width={150} cells={{ data: DateCell }} />
      <Column field="DeviceName" title="Device" cells={{ data: (e) => <td {...e.tdProps}>
        {e.dataItem.DeviceName}
        {e.dataItem.DriverAppDeviceID === current.DriverAppDeviceID && <span className="badge badge-secondary ml-2">Current</span>}
      </td> }} />
      <Column field="AppVersion" title="Version" width={150} cells={{ data: (e) => <td {...e.tdProps}>
        {e.dataItem.AppVersion || 'N/A'}
        {isOutdated(e.dataItem) && <span className="badge badge-warning ml-2">Outdated</span>}
      </td> }} />
      <Column field="Activated" title="Activated" width={120} cells={{ data: (e) => <td {...e.tdProps}>{e.dataItem.Activated ? 'Yes' : e.dataItem.ActivationCode}</td> }} />
      <Column field="HasPushNotifications" title="Notifications" width={130} cells={{ data: (e) => <td {...e.tdProps}>{receivesPush(e.dataItem) ? 'On' : 'Off'}</td> }} />
      <Column field="LastRefreshDateTime" title="Last Opened" width={150} cells={{ data: DateCell }} />
    </Grid>
  </>;

  const dataView = (data: DriverDevicesType) => {
    if (data.Devices.length === 0) {
      return <div className="alert alert-info">No devices found. This driver hasn't set up the driver app.</div>;
    }

    const current = currentDevice(data.Devices);

    return <>
      {currentView(data, current)}
      {data.Devices.length >= 1 && historyView(data.Devices, current)}
    </>;
  }

  return (<>
    <DriverNavBar id={driverIdParam} currentTab="Devices" />
    <Title string="Devices" />
    <br />
    {loading || !data ? loadingView : dataView(data)}
  </>);
}

export default DriverDevices;
